import React from 'react';
import { CheckCircle, Award, Target, Shield, TrendingUp, Users, Info, Building2, UserCheck, ExternalLink } from 'lucide-react';

export default function About() {
  const values = [
    {
      icon: <Award className="h-6 w-6" />,
      title: "Licensed Professionals",
      description: "Fully state-licensed insurance and financial professionals with comprehensive training."
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: "Personalized Service",
      description: "Tailored financial strategies designed specifically for your unique situation."
    },
    {
      icon: <Target className="h-6 w-6" />,
      title: "Goal-Oriented",
      description: "Focused on helping you achieve your specific financial goals and wealth preservation."
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: "Comprehensive Protection",
      description: "Complete living benefits and tax-advantaged protection to secure your family's future."
    }
  ];

  const stats = [
    { number: "15+ Yrs", label: "Combined Leadership Exp.*", sub: "Senior Agency Mentorship" },
    { number: "500+", label: "Clients Served Network-Wide*", sub: "Across Affiliated Partners" },
    { number: "$50M+", label: "Protection Capital Positioned*", sub: "Cumulative Coverage Volume" },
    { number: "100%", label: "Client Service Commitment*", sub: "Dedicated Advisory Focus" }
  ];

  const leadershipPartners = [
    { 
      name: "Vani Financials", 
      role: "Senior Agency Leadership & Mentorship", 
      url: "https://www.vanifinancials.com/" 
    },
    { 
      name: "Daas Financials", 
      role: "Strategic Financial & Underwriting Network", 
      url: "https://daasfinancials.com/" 
    },
    { 
      name: "Vagish Sinha", 
      role: "Executive Industry Mentorship", 
      url: "https://www.linkedin.com/in/vagishsinha/" 
    },
    { 
      name: "Shafin Sadruddin", 
      role: "Field Leadership & Strategy Advisor", 
      url: "https://www.linkedin.com/in/shafin-sadruddin-46339683/" 
    }
  ];

  return (
    <section id="about" className="relative py-24 bg-[#070E1B] text-white overflow-hidden font-opensans">
      {/* ── Background Lighting ───────────────── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-[#C9A84C]/10 rounded-full blur-[150px] animate-pulse-glow" />
        <div className="absolute bottom-10 right-10 w-[500px] h-[500px] bg-[#3D6ACC]/15 rounded-full blur-[150px]" />
        <div 
          className="absolute inset-0 opacity-[0.05]" 
          style={{
            backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px)`,
            backgroundSize: '32px 32px'
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          {/* Left Column - Story & Values */}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs sm:text-sm font-semibold text-[#E8D4A0] tracking-wider uppercase">
                <Award className="w-4 h-4 text-[#C9A84C]" />
                Our Mission & Industry Backing
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-playfair tracking-tight">
                Architecting <span className="text-gradient-gold">Generational Wealth</span> & Legacy Security
              </h2>
              <p className="text-lg text-slate-300 font-opensans leading-relaxed pt-2">
                We are state-licensed life and financial professionals dedicated to empowering business leaders and families with tax-advantaged capital accumulation, Living Benefits protection, and zero-downside market retirement strategies.
              </p>
              <p className="text-base text-slate-300 font-opensans leading-relaxed">
                Backed by senior executive leadership and premier financial networks, we bring decades of collective industry wisdom, carrier relationships, and proven wealth preservation formulas to every client consultation.
              </p>
            </div>

            {/* Values Grid */}
            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              {values.map((value, index) => (
                <div key={index} className="flex items-start gap-3.5 p-4 rounded-xl bg-[#162236]/80 border border-[#C9A84C]/20 backdrop-blur-xl shadow-lg hover:border-[#C9A84C]/50 transition-all duration-300">
                  <div className="p-2.5 rounded-lg bg-[#0D1B2A] text-[#C9A84C] border border-[#C9A84C]/30 shrink-0">
                    {value.icon}
                  </div>
                  <div>
                    <h4 className="font-bold text-sm text-white mb-1">{value.title}</h4>
                    <p className="text-xs text-slate-300 leading-relaxed">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Senior Leadership & Agency Network Card */}
            <div className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl p-6 shadow-xl backdrop-blur-xl space-y-4">
              <div className="flex items-center justify-between border-b border-slate-700/60 pb-3">
                <h4 className="text-base font-bold font-playfair text-[#E8D4A0] flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#C9A84C]" />
                  HGI / Integrity Marketing Group Member Agency
                </h4>
                <span className="px-2.5 py-0.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-[10px] font-bold text-[#E8D4A0]">
                  NPN: 21450620
                </span>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                Grow & Stay Rich functions as a multi-expert advisory network. Contracting & licensing credentials verified via <strong>SureLC</strong> and backed by Hegemon Group International (HGI) / Integrity Marketing Group, alongside esteemed mentorship leadership:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-3 pt-1">
                {leadershipPartners.map((partner, idx) => (
                  <a
                    key={idx}
                    href={partner.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-2.5 bg-[#162236] hover:bg-[#1f2d47] p-3 rounded-xl border border-[#C9A84C]/20 hover:border-[#C9A84C]/60 transition-all group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <UserCheck className="w-4 h-4 text-[#C9A84C] shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-white group-hover:text-[#E8D4A0] transition-colors truncate">
                          {partner.name}
                        </div>
                        <div className="text-[10px] text-slate-400 truncate">{partner.role}</div>
                      </div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-[#C9A84C]/60 group-hover:text-[#C9A84C] shrink-0 transition-colors" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Stats & Why Choose Us */}
          <div className="lg:col-span-5 space-y-8">
            {/* Impact Stats */}
            <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-8 shadow-2xl backdrop-blur-xl space-y-6">
              <div className="text-center space-y-1">
                <h3 className="text-xl font-bold font-playfair text-white">Our Proven Track Record</h3>
                <p className="text-xs text-slate-300">Milestones of network trust & financial protection</p>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center p-4 rounded-xl bg-[#0D1B2A]/80 border border-[#C9A84C]/20">
                    <div className="text-2xl sm:text-3xl font-bold text-gradient-gold mb-1">{stat.number}</div>
                    <div className="text-xs text-[#E8D4A0] font-semibold leading-snug">{stat.label}</div>
                    <div className="text-[10px] text-slate-400 mt-1">{stat.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Why Choose Us Card */}
            <div className="bg-[#0D1B2A]/90 border border-[#C9A84C]/25 rounded-2xl p-8 shadow-2xl backdrop-blur-xl space-y-4">
              <h3 className="text-xl font-bold font-playfair text-white flex items-center gap-2.5">
                <TrendingUp className="h-5 w-5 text-[#C9A84C]" />
                Why Clients Trust GrowAndStayRich
              </h3>
              
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm text-white">Fully Licensed Specialists</h4>
                    <p className="text-xs text-slate-300">State-licensed insurance & annuity professionals</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm text-white">Tailored IRC Sec. 7702 Strategies</h4>
                    <p className="text-xs text-slate-300">Custom Index Universal Life & Fixed Index Annuity plans</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#C9A84C] shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm text-white">Living Benefits Coverage</h4>
                    <p className="text-xs text-slate-300">Access death benefits for chronic or critical illness</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mission Statement Banner */}
        <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-10 sm:p-12 text-center shadow-2xl backdrop-blur-xl relative overflow-hidden">
          <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#C9A84C]/10 rounded-full blur-3xl pointer-events-none" />
          <h3 className="text-2xl sm:text-3xl font-bold font-playfair text-white mb-4">Our Core Philosophy</h3>
          <p className="text-lg sm:text-xl text-slate-200 max-w-4xl mx-auto font-playfair italic leading-relaxed">
            "To empower individuals and families to build lasting wealth through strategic financial planning, comprehensive protection, and tax-efficient strategies that ensure financial security for generations to come."
          </p>
          <div className="mt-8 inline-flex items-center gap-3 px-6 py-2.5 rounded-full bg-[#0D1B2A] border border-[#C9A84C]/40 text-xs sm:text-sm font-semibold text-[#E8D4A0]">
            <Shield className="h-4 w-4 text-[#C9A84C]" />
            <span>State Licensed & Insured • Serving Nationwide</span>
          </div>
        </div>

        {/* ── Regulatory & Network Disclosure Card ───────────────── */}
        <div className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl p-6 sm:p-8 shadow-2xl space-y-3 text-slate-400 text-xs leading-relaxed">
          <div className="flex items-center gap-2 text-[#E8D4A0] font-bold text-xs uppercase tracking-wider">
            <Info className="w-4 h-4 text-[#C9A84C]" />
            *Legal Disclosure & Leadership Network Disclaimer
          </div>
          <p>
            The statistics, figures, and years of experience displayed above represent the cumulative track record, combined agency experience, and collective capital positioning achieved across our broader agency leadership network and strategic mentorship hierarchy, including{' '}
            <a href="https://www.vanifinancials.com/" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline font-semibold">
              Vani Financials
            </a>,{' '}
            <a href="https://daasfinancials.com/" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline font-semibold">
              Daas Financials
            </a>,{' '}
            <a href="https://www.linkedin.com/in/vagishsinha/" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline font-semibold">
              Vagish Sinha
            </a>, and{' '}
            <a href="https://www.linkedin.com/in/shafin-sadruddin-46339683/" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline font-semibold">
              Shafin Sadruddin
            </a>.
          </p>
          <p>
            Individual client advisors are state-licensed insurance and financial professionals operating in active mentorship and collaboration under senior agency leadership. Figures regarding protection capital positioned and clients served reflect aggregate network metrics across participating insurance carrier partners. Financial strategy outcomes, tax-advantaged growth, and policy benefits depend on individual qualifications, underwriting approval, carrier contract terms, and state regulations. GrowAndStayRich provides financial education and policy illustration services through state-licensed agents.
          </p>
        </div>
      </div>
    </section>
  );
}